import {
  Box,
  Divider,
  Flex,
  Heading,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import {
  PlatformRequirementDocumentVerdict,
  PlatformRequirementIssueStatus,
} from '@client/graphql/__generated__/types';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  FileIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { FC, MouseEvent, ReactElement, ReactNode, useId } from 'react';
import { match, P } from 'ts-pattern';
import { formatAttemptTimestamp as formatChecklistTimestamp } from './autopilotAttempt.format';
import {
  ChecklistChipTone,
  ChecklistContextDocumentView,
  ChecklistContractDocumentView,
  ChecklistEvaluationDocumentView,
  ChecklistEvaluationStatus,
  ChecklistGroupedEvaluationDocumentView,
  ChecklistIssueView,
  DocumentBadgeTone,
  PRETTY_WRAP,
} from './checklistIssue.types';
import {
  ChecklistCategoryChip,
  ChecklistFormChip,
  ChecklistSatisfiedBy,
  ChecklistVerdictChip,
} from './checklistIssueChips';
import { ChecklistGroup, groupChecklistIssues } from './checklistIssueGrouping';
import { ChecklistIssueTooltipContent } from './ChecklistIssueTooltipContent';
import { formatPlatformRequirementIssueStatus } from './platformRequirementIssueStatus.format';
import { formatPlatformRequirementProblemType } from './platformRequirementProblemType.format';
import { TruncatedDocumentName } from './TruncatedDocumentName';

export type {
  ChecklistContextDocumentView,
  ChecklistContractDocumentView,
  ChecklistEvaluationDocumentView,
  ChecklistEvaluationStatus,
  ChecklistGroupedEvaluationDocumentView,
  ChecklistIssueView,
  DocumentBadgeTone,
};

/**
 * How each checklist group is drawn.
 *
 * The collapsed variants deliberately differ: a checklist issue should read
 * like the validation rows it sits beside, and those rows are shaped
 * differently on each surface. The inline evidence behind them is identical.
 *
 * - `expanded`: the internal debug drawer's full evaluation detail.
 * - `compact`: extension side panel / embedded drawer. One line, mirroring
 *   {@link BaseValidationItem}.
 * - `suggestion`: agent compliance review page. Two lines inside the
 *   suggestions card, mirroring `DocumentSuggestionsList`.
 */
export type ChecklistIssueRowVariant = 'expanded' | 'compact' | 'suggestion';

/**
 * Agent-only actions. Provided by the agent compliance review surface.
 *
 * Both callbacks take the whole group's issues rather than one issue at a time:
 * the affordances are group-scoped, and the caller prompts for a single ignore
 * reason covering the group. Collecting that reason needs a modal, which belongs
 * on the agent surface — this component is also rendered by the internal app, so
 * it must not instantiate one itself.
 */
interface ChecklistIssueActions {
  /**
   * The agent asked to ignore the group's open issues. Reporting the intent
   * rather than performing it is deliberate: the caller prompts for the reason
   * and owns the write, so this component never has to know about either.
   */
  onIgnore: (issues: ChecklistIssueView[]) => void;
  /** Un-ignores every agent-ignored issue in the group. */
  onUndoIgnore: (issues: ChecklistIssueView[]) => void;
  isIgnoring: boolean;
  /** The exact action and public issues currently being mutated. */
  pendingAction?: {
    action: 'ignore' | 'undo';
    publicIssueIds: string[];
  } | null;
}

type ChecklistIssuesListProps = {
  issues: ChecklistIssueView[];
  selectedIngestionDocumentId?: string;
  selectedPlatformDocumentId?: string | null;
  /**
   * Evaluation state for the selected checklist item. Distinguishes an honest
   * "no data" state from a positive clean state and from a failed evaluation.
   */
  evaluationStatus: ChecklistEvaluationStatus;
  /** How each group is drawn. Defaults to the internal expanded card. */
  rowVariant?: ChecklistIssueRowVariant;
  /** Selected checklist item name, shown in the no-issue empty state. */
  checklistItemName?: string | null;
  /**
   * The selected checklist item's allowed categories/forms, shown as the
   * "Needs at least one attachment classified with:" box in the no-issue empty
   * state. Known on the live surface; absent on the persisted no-data surface.
   */
  requiredDocumentCategories?: string[];
  requiredForms?: string[];
  /** Attached docs shown when there is no current issue for the selected item. */
  contextDocuments?: ChecklistContextDocumentView[];
  /**
   * Internal-only: show the "Evaluated <timestamp>" line inside each group.
   *
   * Normally off, because the card header states one timestamp for the whole
   * card. The caller turns it on only when the groups came from different
   * evaluation runs, where no single header timestamp would be true. External
   * surfaces never show it: the persisted history is incomplete there and the
   * timestamp is not agent-facing.
   */
  showEvaluatedAt?: boolean;
  /**
   * Labels a fully-handled checklist item with who handled it, matching the chip
   * validation suggestions already carry. Resubmitted agent issues retain the
   * "Agent ignored" attribution. Both reviewer and agent-review surfaces opt in
   * so every crossed-out row explains why it was handled.
   */
  showStatusChip?: boolean;
  onOpenDocument?: (document: ChecklistEvaluationDocumentView) => void;
} & ({ actions?: never } | { actions: ChecklistIssueActions });

/**
 * Resolves the status badge for one attached document.
 *
 * A recorded `MATCHED` verdict is positive evidence, so it says "Matched"
 * plainly — the same word the recorded-evidence panel uses. The hedged "No
 * mismatch" is reserved for the case one step weaker: no verdict at all, but a
 * completed `evaluated` state, from which cleanliness is only *inferred*.
 * Without even that, the badge is a neutral "No data" rather than a false
 * assertion that the attachment is clean (BLO-13160).
 */
const getDocumentStatusBadge = (input: {
  isProblemDocument: boolean;
  verdict?: PlatformRequirementDocumentVerdict | null;
  evaluationStatus: ChecklistEvaluationStatus;
}): { label: string; tone: ChecklistChipTone } =>
  match(input)
    .with({ verdict: PlatformRequirementDocumentVerdict.MISMATCHED }, () => ({
      label: 'Mismatch',
      tone: 'error' as const,
    }))
    .with({ verdict: PlatformRequirementDocumentVerdict.MATCHED }, () => ({
      label: 'Matched',
      tone: 'success' as const,
    }))
    .with({ verdict: PlatformRequirementDocumentVerdict.UNRESOLVED }, () => ({
      label: 'Unresolved',
      tone: 'warning' as const,
    }))
    .with({ verdict: PlatformRequirementDocumentVerdict.UNCONFIGURED }, () => ({
      label: 'No requirement',
      tone: 'outline' as const,
    }))
    .with({ isProblemDocument: true }, () => ({
      label: 'Mismatch',
      tone: 'error' as const,
    }))
    .with({ evaluationStatus: 'evaluated' }, () => ({
      label: 'No mismatch',
      tone: 'success' as const,
    }))
    .otherwise(() => ({ label: 'No data', tone: 'neutral' as const }));

const DocumentStatusBadge: FC<{
  isProblemDocument: boolean;
  verdict?: PlatformRequirementDocumentVerdict | null;
  evaluationStatus: ChecklistEvaluationStatus;
}> = ({ isProblemDocument, verdict, evaluationStatus }) => {
  const { label, tone } = getDocumentStatusBadge({
    isProblemDocument,
    verdict,
    evaluationStatus,
  });

  return <ChecklistVerdictChip label={label} tone={tone} />;
};

/**
 * The severity a whole group leads with, read off the evidence beneath it.
 *
 * A group is a mismatch issue by construction, but not every one of them is a
 * *mismatch*: when nothing in the attachment could be classified, every
 * document comes back `UNRESOLVED` and nothing was actually compared. Calling
 * that a mismatch overstates what the evaluation found, and it is the
 * difference between "the wrong form is attached" and "we could not read the
 * form" — only the first is something a reviewer can act on.
 */
const getGroupSeverity = (
  group: ChecklistGroup
): { label: string; tone: ChecklistChipTone } => {
  const hasMismatch = group.evaluationDocuments.some(
    (document) =>
      document.isProblemDocument ||
      document.verdict === PlatformRequirementDocumentVerdict.MISMATCHED
  );
  const isEntirelyUnresolved =
    !hasMismatch &&
    group.evaluationDocuments.length > 0 &&
    group.evaluationDocuments.every(
      (document) =>
        document.verdict === PlatformRequirementDocumentVerdict.UNRESOLVED
    );

  // Anything else — including a group with no recorded evidence — keeps the
  // issue's own nature. It was raised as a mismatch; absent evidence to soften
  // that, saying so is the truthful default.
  return isEntirelyUnresolved
    ? { label: 'Unresolved', tone: 'warning' }
    : { label: 'Mismatch', tone: 'error' };
};

interface ChecklistGroupLifecycleSummary {
  displayStatus: PlatformRequirementIssueStatus | null;
}

const CHECKLIST_ISSUE_STATUS_PRIORITY: Record<
  PlatformRequirementIssueStatus,
  number
> = {
  [PlatformRequirementIssueStatus.OPEN]: 3,
  [PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER]: 2,
  [PlatformRequirementIssueStatus.RESUBMITTED]: 1,
  [PlatformRequirementIssueStatus.IGNORED_BY_AGENT]: 0,
};

/**
 * Builds the compact lifecycle summary shown for one checklist group. This is
 * an intentional client-side display policy: any open issue keeps the group
 * open and suppresses the chip; otherwise mixed handled statuses collapse to
 * reviewer ignored, then resubmitted, then agent ignored.
 */
const summarizeGroupLifecycle = (
  issues: ChecklistIssueView[]
): ChecklistGroupLifecycleSummary => {
  const displayStatus = issues.reduce<PlatformRequirementIssueStatus | null>(
    (currentStatus, issue) =>
      currentStatus == null ||
      CHECKLIST_ISSUE_STATUS_PRIORITY[issue.status] >
        CHECKLIST_ISSUE_STATUS_PRIORITY[currentStatus]
        ? issue.status
        : currentStatus,
    null
  );

  return match(displayStatus)
    .with(null, PlatformRequirementIssueStatus.OPEN, () => ({
      displayStatus: null,
    }))
    .with(
      PlatformRequirementIssueStatus.IGNORED_BY_AGENT,
      PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER,
      PlatformRequirementIssueStatus.RESUBMITTED,
      (status) => ({ displayStatus: status })
    )
    .exhaustive();
};

/**
 * Solid "Agent ignored" / "Ignored by reviewer" chip. A resubmitted checklist
 * issue remains labelled "Agent ignored" to match validation errors: the chip
 * describes who handled the issue, while resubmission remains lifecycle state.
 * Kept unstruck and at full contrast (unlike the faded row it sits in) so the
 * viewer can tell at a glance who disposed of the issue — the checklist
 * counterpart to the validation row's chip in {@link BaseValidationItem}.
 */
const formatChecklistIssueStatus = (
  status: PlatformRequirementIssueStatus
): string =>
  match(status)
    .with(
      PlatformRequirementIssueStatus.IGNORED_BY_AGENT,
      PlatformRequirementIssueStatus.RESUBMITTED,
      () => 'Agent ignored'
    )
    .with(
      PlatformRequirementIssueStatus.OPEN,
      PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER,
      formatPlatformRequirementIssueStatus
    )
    .exhaustive();

const ChecklistIssueStatusChip: FC<{
  status: PlatformRequirementIssueStatus;
}> = ({ status }) => (
  <Box
    bg="compliance.tag.bg"
    borderRadius="xs"
    color="compliance.text.secondary"
    flexShrink={0}
    fontSize="xs"
    fontWeight="medium"
    px={1.5}
    py={0.5}
    whiteSpace="nowrap"
  >
    {formatChecklistIssueStatus(status)}
  </Box>
);

/** Uppercase caption above a block, with an optional count beside it. */
const ChecklistBlockCaption: FC<{ label: string; count?: string }> = ({
  label,
  count,
}) => (
  <Flex align="baseline" gap={2}>
    <Text
      color="compliance.text.muted"
      fontSize="10.5px"
      fontWeight="semibold"
      letterSpacing="1px"
      textTransform="uppercase"
    >
      {label}
    </Text>
    {count != null ? (
      <Text color="compliance.text.muted" fontSize="11.5px">
        {count}
      </Text>
    ) : null}
  </Flex>
);

/**
 * Icon width plus its gap, so anything on a following line hangs under the
 * filename rather than under the icon.
 */
const EVIDENCE_INDENT = '21px';

/**
 * One attached-document row of checklist evidence.
 *
 * Stacked, not single-line: this renders in a 320px sidebar, where a filename
 * and a trailing verdict chip cannot share a row without the name truncating
 * away the suffix that identifies it. The name gets the full width and
 * everything else — verdict, selection, identified classifications — hangs
 * beneath it, indented past the icon.
 *
 * The name still middle-truncates, because attachment names share their first
 * forty characters even with the whole column to themselves.
 */
const AttachedDocumentRow: FC<{
  documentName: string;
  isProblemDocument: boolean;
  verdict?: PlatformRequirementDocumentVerdict | null;
  contractDocuments?: ChecklistContractDocumentView[] | null;
  isSelected: boolean;
  evaluationStatus: ChecklistEvaluationStatus;
  /**
   * Off for the passing-checks list, where every row is a plain filename and
   * a file icon on each would be decoration repeated N times. Evidence rows
   * keep it — there the icon marks each attachment in a mixed block.
   */
  showIcon?: boolean;
  onOpen?: () => void;
}> = ({
  documentName,
  isProblemDocument,
  verdict,
  contractDocuments,
  isSelected,
  evaluationStatus,
  showIcon = true,
  onOpen,
}) => (
  <Flex
    // Hovering a selected row must not wash out the tint that says it is
    // selected — the pointer is already the weaker of the two signals.
    _hover={{
      bg: isSelected
        ? 'compliance.checklist.row.selected.bg'
        : 'compliance.hover.bg',
    }}
    aria-label={`Document ${documentName}`}
    bg={isSelected ? 'compliance.checklist.row.selected.bg' : 'transparent'}
    borderColor={
      isSelected ? 'compliance.checklist.row.selected.border' : 'transparent'
    }
    borderRadius="sm"
    borderWidth="1px"
    direction="column"
    gap={1}
    mx="-8px"
    px="8px"
    py="6px"
    role="group"
  >
    <Flex align="center" gap={2} minW={0}>
      {showIcon ? (
        <Icon
          as={FileIcon}
          boxSize="13px"
          color="compliance.icon.secondary"
          flexShrink={0}
        />
      ) : null}
      <Flex
        color="compliance.text.primary"
        flex={1}
        fontSize="13px"
        lineHeight={1.3}
        minW={0}
      >
        <TruncatedDocumentName documentName={documentName} />
      </Flex>
    </Flex>

    {/* Everything about the row hangs below the filename, indented past the
        icon. A 320px column has no room for a trailing chip, and truncating
        the name to make room would hide the half that identifies it. */}
    <Flex
      align="center"
      gap="6px"
      ml={showIcon ? EVIDENCE_INDENT : 0}
      wrap="wrap"
    >
      {isSelected ? (
        <ChecklistVerdictChip label="Selected" tone="selected" />
      ) : null}
      <DocumentStatusBadge
        evaluationStatus={evaluationStatus}
        isProblemDocument={isProblemDocument}
        verdict={verdict}
      />
      {onOpen ? (
        <Flex
          align="center"
          as="button"
          color="compliance.link.fg"
          flexShrink={0}
          fontSize="12.5px"
          fontWeight="medium"
          gap={1}
          type="button"
          onClick={onOpen}
        >
          Open
          <Icon as={ExternalLinkIcon} boxSize="12px" />
        </Flex>
      ) : null}
    </Flex>

    {contractDocuments != null ? (
      <Box ml={showIcon ? EVIDENCE_INDENT : 0}>
        <ChecklistBlockCaption
          count={String(contractDocuments.length)}
          label="Contract documents"
        />
        {contractDocuments.length === 0 ? (
          <Text
            color="compliance.text.muted"
            fontSize="11.5px"
            fontStyle="italic"
            mt="2px"
          >
            No contract documents identified
          </Text>
        ) : (
          <Flex gap={1.5} mt="2px" wrap="wrap">
            {contractDocuments.map((contractDocument, index) => (
              <Flex key={`cd-${index}`} align="center" gap={1.5} wrap="wrap">
                {/* Both halves always render something. A classification with
                    neither a form nor a category is real evaluation evidence,
                    and silently drawing nothing for it loses the row. */}
                {contractDocument.form ? (
                  <ChecklistFormChip
                    form={{
                      code: contractDocument.form,
                      name: contractDocument.formName,
                    }}
                  />
                ) : (
                  <Text
                    color="compliance.text.muted"
                    fontSize="11.5px"
                    fontStyle="italic"
                  >
                    no form
                  </Text>
                )}
                {contractDocument.documentCategory ? (
                  <ChecklistCategoryChip
                    category={contractDocument.documentCategory}
                  />
                ) : (
                  <Text
                    color="compliance.text.muted"
                    fontSize="11.5px"
                    fontStyle="italic"
                  >
                    no category
                  </Text>
                )}
              </Flex>
            ))}
          </Flex>
        )}
      </Box>
    ) : null}
  </Flex>
);

/**
 * Per-group state every variant needs: which issues are actionable, whether the
 * group reads as handled, and the distinct reasons given for handling it.
 */
interface ChecklistGroupState {
  openIssues: ChecklistIssueView[];
  ignoredIssues: ChecklistIssueView[];
  displayStatus: PlatformRequirementIssueStatus | null;
  isResolved: boolean;
  ignoreReasons: string[];
}

/**
 * The "Ignored by <actor> · <when>" line beneath a handled group.
 *
 * Reads the first issue that carries either half — a group is normally handled
 * in one action, so the halves agree. Returns null when neither is recorded
 * rather than printing a sentence that attributes nothing.
 */
const buildIgnoreAttribution = (
  issues: ChecklistIssueView[]
): string | null => {
  const attributed = issues.find(
    (issue) => issue.ignoredBy != null || issue.ignoredAt != null
  );
  if (attributed == null) {
    return null;
  }

  const at = formatChecklistTimestamp(attributed.ignoredAt);

  return match({ by: attributed.ignoredBy, at })
    .with(
      { by: P.string, at: P.string },
      ({ by, at: when }) => `Ignored by ${by} · ${when}`
    )
    .with({ by: P.string }, ({ by }) => `Ignored by ${by}`)
    .with({ at: P.string }, ({ at: when }) => `Ignored · ${when}`)
    .otherwise(() => null);
};

const deriveGroupState = (group: ChecklistGroup): ChecklistGroupState => {
  const { displayStatus } = summarizeGroupLifecycle(group.issues);

  return {
    openIssues: group.issues.filter(
      (issue) =>
        issue.isAgentActionable &&
        issue.status === PlatformRequirementIssueStatus.OPEN
    ),
    // Undo stays bound to these: only an agent ignore is reversible here.
    ignoredIssues: group.issues.filter(
      (issue) =>
        issue.isAgentActionable &&
        issue.status === PlatformRequirementIssueStatus.IGNORED_BY_AGENT
    ),
    displayStatus,
    isResolved: displayStatus != null,
    // One reason normally covers the group (a single Ignore applies the same
    // text to every open issue), but a group can hold issues ignored in
    // separate rounds — so collect the distinct ones rather than the first.
    ignoreReasons: [
      ...new Set(
        group.issues
          .map((issue) => issue.ignoreReason?.trim())
          .filter((reason): reason is string => Boolean(reason))
      ),
    ],
  };
};

/**
 * Agent ignore explanation, appended to the checklist evidence.
 * Only meaningful once handled WITH a reason; an open group has nothing to
 * explain.
 */
const IgnoreReasons: FC<{ reasons: string[] }> = ({ reasons }) => (
  <Box>
    <Heading
      color="compliance.tooltip.text"
      mb={1}
      opacity={0.6}
      size="smallUppercase"
    >
      Agent&apos;s reason for ignoring
    </Heading>
    <VStack align="stretch" spacing={1}>
      {reasons.map((reason) => (
        <Text key={reason} color="compliance.tooltip.text" fontSize="sm">
          {reason}
        </Text>
      ))}
    </VStack>
  </Box>
);

/**
 * Adds an explicit inline evidence disclosure to a collapsed checklist row.
 * The native button makes the evidence available to keyboard and touch users,
 * while keeping row actions such as Ignore and Undo as separate controls.
 */
const ChecklistRowDisclosure: FC<{
  group: ChecklistGroup;
  ignoreReasons: string[];
  children: ReactElement;
}> = ({ group, ignoreReasons, children }) => {
  const { isOpen, onToggle } = useDisclosure();
  const evidenceId = useId();
  const hasEvidence = !(
    group.allowedDocumentCategories.length === 0 &&
    group.allowedForms.length === 0 &&
    group.evaluationDocuments.length === 0 &&
    ignoreReasons.length === 0
  );

  if (!hasEvidence) {
    return children;
  }

  const action = isOpen ? 'Hide' : 'View';

  return (
    <VStack align="stretch" spacing={isOpen ? 2 : 0} width="100%">
      <Flex align="center" gap={2} justify="space-between">
        <Box flex={1} minW={0}>
          {children}
        </Box>
        <IconButton
          aria-controls={evidenceId}
          aria-expanded={isOpen}
          aria-label={`${action} evidence for ${group.checklistItemName}`}
          color="compliance.text.muted"
          flexShrink={0}
          icon={<Icon as={isOpen ? ChevronDownIcon : ChevronRightIcon} />}
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
        />
      </Flex>
      {isOpen ? (
        <Box
          aria-label={`Evidence for ${group.checklistItemName}`}
          bg="compliance.tooltip.bg"
          border="1px solid"
          borderColor="compliance.tooltip.border"
          borderRadius="md"
          color="compliance.tooltip.text"
          id={evidenceId}
          p={3}
          role="region"
        >
          <VStack align="stretch" spacing={3}>
            <ChecklistIssueTooltipContent
              allowedDocumentCategories={group.allowedDocumentCategories}
              allowedForms={group.allowedForms}
              checklistItemName={group.checklistItemName}
              evaluationDocuments={group.evaluationDocuments}
            />
            {ignoreReasons.length > 0 ? (
              <>
                <Divider
                  aria-label="Agent reason separator"
                  borderColor="compliance.tooltip.text"
                  opacity={0.2}
                />
                <IgnoreReasons reasons={ignoreReasons} />
              </>
            ) : null}
          </VStack>
        </Box>
      ) : null}
    </VStack>
  );
};

/**
 * Extension side panel / embedded drawer row. One line, matching the validation
 * rows directly above it: warning icon, checklist item, then the short reason.
 *
 * The item name leads because it is the part that varies — every checklist row
 * currently carries the same reason, so reason-first would give a column of
 * identical text to scan past.
 */
const CompactChecklistRow: FC<{
  group: ChecklistGroup;
  state: ChecklistGroupState;
  showStatusChip: boolean;
}> = ({ group, state, showStatusChip }) => {
  const { isResolved, displayStatus, ignoreReasons } = state;

  return (
    <ChecklistRowDisclosure group={group} ignoreReasons={ignoreReasons}>
      <Flex
        _hover={{ bg: 'compliance.hover.bg' }}
        align="center"
        borderRadius="sm"
        gap={2}
        mx={-2}
        px={2}
        py={1}
      >
        <Icon
          as={TriangleAlertIcon}
          color={
            isResolved ? 'compliance.text.muted' : 'compliance.status.error'
          }
          flexShrink={0}
        />
        {/* One text flow, not two flex siblings. The panel is narrow and
            checklist item names are long, so as siblings each would wrap in its
            own column and strand the "-" at the start of a line, where it reads
            as a stray bullet rather than a separator. Nesting the reason mirrors
            how {@link BaseValidationItem} carries its subtitle. */}
        <Text
          color={
            isResolved ? 'compliance.text.muted' : 'compliance.text.primary'
          }
          fontSize="sm"
          fontWeight="semibold"
          textDecoration={isResolved ? 'line-through' : undefined}
        >
          {group.checklistItemName}
          <Text as="span" color="compliance.text.muted" fontWeight="normal">
            {/* Non-breaking space before the separator so a wrap can only fall
                after it. A line that opens with "- " reads as a stray bullet. */}
            {'\u00a0- '}
            {formatPlatformRequirementProblemType(group.problemType)}
          </Text>
        </Text>
        {showStatusChip && displayStatus != null ? (
          <ChecklistIssueStatusChip status={displayStatus} />
        ) : null}
      </Flex>
    </ChecklistRowDisclosure>
  );
};

/**
 * Agent compliance review row. Two lines inside the suggestions card, matching
 * `DocumentSuggestionsList`: the reason reads as the headline and the checklist
 * item takes the meta slot a validation suggestion uses for its page label,
 * alongside the Ignore/Undo affordance.
 */
const SuggestionChecklistRow: FC<{
  group: ChecklistGroup;
  state: ChecklistGroupState;
  showStatusChip: boolean;
  actions?: ChecklistIssueActions;
}> = ({ group, state, showStatusChip, actions }) => {
  const {
    isResolved,
    displayStatus,
    ignoreReasons,
    openIssues,
    ignoredIssues,
  } = state;
  const pendingAction = actions?.pendingAction;
  const rowPendingAction = pendingAction?.publicIssueIds.some((publicIssueId) =>
    group.issues.some((issue) => issue.publicIssueId === publicIssueId)
  )
    ? pendingAction.action
    : null;

  // stopPropagation so the affordance doesn't also trigger any row-level
  // handler the card adds later.
  const handleIgnore = (event: MouseEvent): void => {
    event.stopPropagation();
    if (actions && !actions.isIgnoring) {
      actions.onIgnore(openIssues);
    }
  };
  const handleUndoIgnore = (event: MouseEvent): void => {
    event.stopPropagation();
    if (actions && !actions.isIgnoring) {
      actions.onUndoIgnore(ignoredIssues);
    }
  };

  const canIgnore = actions != null && openIssues.length > 0;
  const canUndo = actions != null && ignoredIssues.length > 0;

  return (
    <ChecklistRowDisclosure group={group} ignoreReasons={ignoreReasons}>
      <Flex
        align="flex-start"
        borderRadius="6px"
        gap={2}
        mx={-2}
        opacity={isResolved ? 0.6 : undefined}
        px={2}
        py={1}
      >
        <Icon
          as={TriangleAlertIcon}
          boxSize="16px"
          color={
            isResolved ? 'compliance.text.muted' : 'compliance.status.warning'
          }
          flexShrink={0}
          mt={1}
        />
        <Flex direction="column" gap={1} minW={0}>
          <Flex align="center" gap={2}>
            <Text
              color={
                isResolved ? 'compliance.text.muted' : 'compliance.text.primary'
              }
              fontSize="sm"
              textDecoration={isResolved ? 'line-through' : undefined}
            >
              {formatPlatformRequirementProblemType(group.problemType)}
            </Text>
            {showStatusChip && displayStatus != null ? (
              <ChecklistIssueStatusChip status={displayStatus} />
            ) : null}
          </Flex>
          {/* Semantic token rather than the `whiteAlpha.600` its sibling
            DocumentSuggestionsList uses: that list is agent-page-only, but this
            component is shared and has to survive the internal light theme.
            Wraps, too, so a long checklist item pushes "• Ignore" onto the next
            line instead of leaving it floating beside a wrapped block — item
            names here are far longer than the page label the validation
            counterpart shows, which never needed to wrap. */}
          <Flex
            align="center"
            color="compliance.text.muted"
            fontSize="sm"
            gap={1.5}
            wrap="wrap"
          >
            <Text>{group.checklistItemName}</Text>
            {rowPendingAction != null ? (
              <>
                <Text aria-hidden>•</Text>
                <Flex align="center" gap={1.5}>
                  <Spinner size="xs" />
                  <Text>
                    {rowPendingAction === 'undo' ? 'Undoing' : 'Ignoring'}
                  </Text>
                </Flex>
              </>
            ) : null}
            {actions && !actions.isIgnoring && canIgnore ? (
              <>
                <Text aria-hidden>•</Text>
                <Text
                  _hover={{ color: 'compliance.text.secondary' }}
                  aria-label="Ignore checklist issue"
                  as="button"
                  cursor="pointer"
                  textDecoration="underline"
                  type="button"
                  onClick={handleIgnore}
                >
                  Ignore
                </Text>
              </>
            ) : null}
            {actions && !actions.isIgnoring && canUndo ? (
              <>
                <Text aria-hidden>•</Text>
                <Text
                  _hover={{ color: 'compliance.text.secondary' }}
                  aria-label="Undo ignore checklist issue"
                  as="button"
                  cursor="pointer"
                  textDecoration="underline"
                  type="button"
                  onClick={handleUndoIgnore}
                >
                  Undo
                </Text>
              </>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
    </ChecklistRowDisclosure>
  );
};

/** The internal debug drawer's full, neutrally grouped evaluation detail. */
const ExpandedChecklistGroup: FC<{
  group: ChecklistGroup;
  state: ChecklistGroupState;
  evaluationStatus: ChecklistEvaluationStatus;
  selectedIngestionDocumentId?: string;
  selectedPlatformDocumentId?: string | null;
  showEvaluatedAt: boolean;
  showStatusChip: boolean;
  actions?: ChecklistIssueActions;
  onOpenDocument?: (document: ChecklistEvaluationDocumentView) => void;
}> = ({
  group,
  state,
  evaluationStatus,
  selectedIngestionDocumentId,
  selectedPlatformDocumentId,
  showEvaluatedAt,
  showStatusChip,
  actions,
  onOpenDocument,
}) => {
  const {
    isResolved,
    displayStatus,
    ignoreReasons,
    openIssues,
    ignoredIssues,
  } = state;
  const pendingAction = actions?.pendingAction;
  const rowPendingAction = pendingAction?.publicIssueIds.some((publicIssueId) =>
    group.issues.some((issue) => issue.publicIssueId === publicIssueId)
  )
    ? pendingAction.action
    : null;
  const severity = getGroupSeverity(group);
  // Stated outright rather than left to be tallied off the row chips: "3
  // attached" answers a different question from "how many actually failed".
  const mismatchCount = group.evaluationDocuments.filter(
    (document) =>
      document.isProblemDocument ||
      document.verdict === PlatformRequirementDocumentVerdict.MISMATCHED
  ).length;
  const formattedEvaluatedAt = formatChecklistTimestamp(group.evaluatedAt);
  // "Ignored by J. Reyes · Aug 29, 4:20 PM", degrading to whichever half is
  // known. Silent when neither is: a bare "Ignored by" attributes nothing.
  const ignoreAttribution = buildIgnoreAttribution(group.issues);

  const handleIgnore = (event: MouseEvent): void => {
    event.stopPropagation();
    if (actions && !actions.isIgnoring) {
      actions.onIgnore(openIssues);
    }
  };
  const handleUndoIgnore = (event: MouseEvent): void => {
    event.stopPropagation();
    if (actions && !actions.isIgnoring) {
      actions.onUndoIgnore(ignoredIssues);
    }
  };

  return (
    <VStack
      align="stretch"
      bg="compliance.document.bg"
      borderRadius="sm"
      px={3}
      py="11px"
      spacing="10px"
    >
      {/* The severity chip sits above the name rather than beside it: a
          checklist item name needs the full 320px to wrap readably, and a chip
          competing for the same line would squeeze it into four words a line. */}
      <Flex direction="column" gap="5px">
        {group.checklistSectionName ? (
          <Box aria-label="QA section" role="group">
            <ChecklistBlockCaption label="QA section" />
            <Text
              color="compliance.text.secondary"
              fontSize="13.5px"
              lineHeight={1.35}
              sx={PRETTY_WRAP}
            >
              {group.checklistSectionName}
            </Text>
          </Box>
        ) : null}
        {/* Severity leads the group; the grey "Ignored" chip replaces it once
            the group is handled, with the attribution line at the foot of the
            block naming who did it. */}
        <Flex align="center" alignSelf="flex-start">
          {isResolved ? (
            showStatusChip && displayStatus != null ? (
              <ChecklistVerdictChip
                label={formatChecklistIssueStatus(displayStatus)}
                tone="neutral"
              />
            ) : null
          ) : (
            <ChecklistVerdictChip label={severity.label} tone={severity.tone} />
          )}
        </Flex>
        <Tooltip
          hasArrow
          bg="compliance.tooltip.bg"
          isDisabled={!isResolved || ignoreReasons.length === 0}
          label={
            ignoreReasons.length > 0 ? (
              <IgnoreReasons reasons={ignoreReasons} />
            ) : undefined
          }
          variant="muted"
        >
          <Text
            color={
              isResolved ? 'compliance.text.struck' : 'compliance.text.primary'
            }
            fontSize="14.5px"
            fontWeight="semibold"
            lineHeight={1.3}
            sx={PRETTY_WRAP}
            textDecoration={isResolved ? 'line-through' : undefined}
            textDecorationColor="compliance.text.struck.line"
          >
            {group.checklistItemName}
          </Text>
        </Tooltip>
      </Flex>

      {/* A handled group keeps its full evidence. The design draws it collapsed
          to a record, but this is a debug surface: the requirement and the
          per-attachment verdicts are usually the reason to open a decision that
          has already been made. The attribution line is appended to it. */}
      <>
        <ChecklistSatisfiedBy
          allowedDocumentCategories={group.allowedDocumentCategories}
          allowedForms={group.allowedForms}
        />

        {group.evaluationDocuments.length > 0 ? (
          <Box>
            <ChecklistBlockCaption
              count={`${group.evaluationDocuments.length} attached · ${
                mismatchCount === 1
                  ? '1 mismatch'
                  : `${mismatchCount} mismatches`
              }`}
              label="Evidence"
            />
            <VStack align="stretch" mt={1} spacing="2px">
              {group.evaluationDocuments.map((document) => (
                <AttachedDocumentRow
                  key={document.renderKey}
                  contractDocuments={document.contractDocuments}
                  documentName={document.documentName}
                  evaluationStatus={evaluationStatus}
                  isProblemDocument={document.isProblemDocument}
                  isSelected={
                    (document.ingestionDocumentId != null &&
                      document.ingestionDocumentId ===
                        selectedIngestionDocumentId) ||
                    (document.platformDocumentId != null &&
                      document.platformDocumentId ===
                        selectedPlatformDocumentId)
                  }
                  verdict={document.verdict}
                  onOpen={
                    onOpenDocument ? () => onOpenDocument(document) : undefined
                  }
                />
              ))}
            </VStack>
          </Box>
        ) : null}

        {showEvaluatedAt && formattedEvaluatedAt ? (
          <Text color="compliance.text.muted" fontSize="12.5px">
            Evaluated {formattedEvaluatedAt}
          </Text>
        ) : null}

        {rowPendingAction != null ? (
          <Flex
            align="center"
            color="compliance.text.muted"
            fontSize="12.5px"
            gap={2}
          >
            <Spinner size="xs" />
            {rowPendingAction === 'undo' ? 'Undoing' : 'Ignoring'}
          </Flex>
        ) : null}
        {actions && !actions.isIgnoring && openIssues.length > 0 ? (
          <Flex justify="flex-end">
            <Text
              _hover={{
                bg: 'compliance.hover.bg',
                color: 'compliance.text.primary',
              }}
              aria-label="Ignore checklist issue"
              as="button"
              borderColor="compliance.card.border"
              borderRadius="sm"
              borderWidth="1px"
              color="compliance.text.secondary"
              fontSize="13px"
              fontWeight="medium"
              px="11px"
              py="5px"
              type="button"
              onClick={handleIgnore}
            >
              Ignore
            </Text>
          </Flex>
        ) : null}
        {/* Undo alone: the attribution chip above already names who ignored
            this, and repeating it beside the link says the same thing twice. */}
        {actions && !actions.isIgnoring && ignoredIssues.length > 0 ? (
          <Text
            alignSelf="flex-start"
            aria-label="Undo ignore checklist issue"
            as="button"
            color="compliance.link.fg"
            fontSize="12.5px"
            fontWeight="medium"
            type="button"
            onClick={handleUndoIgnore}
          >
            Undo
          </Text>
        ) : null}
        {/* No affordance and no explanation would read as an oversight. The
              group is open, but nothing can be done with it until the
              attachment is classified. */}
        {actions == null && severity.tone === 'warning' && !isResolved ? (
          <Text
            color="compliance.text.muted"
            fontSize="12.5px"
            lineHeight={1.4}
            sx={PRETTY_WRAP}
          >
            No action available while classification is pending.
          </Text>
        ) : null}
        {ignoreAttribution != null ? (
          <Text color="compliance.text.muted" fontSize="12.5px">
            {ignoreAttribution}
          </Text>
        ) : null}
      </>
    </VStack>
  );
};

/**
 * Read-only checklist mismatch list shared across the internal (light) and
 * agent/external (dark) compliance surfaces. Themed via `compliance.*` tokens.
 * The optional `actions` branch adds the agent-only Ignore/Undo affordances.
 *
 * `rowVariant` picks how each group is drawn — see
 * {@link ChecklistIssueRowVariant}.
 */
export const ChecklistIssuesList: FC<ChecklistIssuesListProps> = (props) => {
  const {
    issues,
    selectedIngestionDocumentId,
    selectedPlatformDocumentId,
    evaluationStatus,
    rowVariant = 'expanded',
    checklistItemName,
    requiredDocumentCategories = [],
    requiredForms = [],
    contextDocuments = [],
    showEvaluatedAt = false,
    showStatusChip = false,
    onOpenDocument,
  } = props;
  const actions = 'actions' in props ? props.actions : undefined;
  const isExpanded = rowVariant === 'expanded';

  if (evaluationStatus === 'unavailable') {
    return (
      <Text color="compliance.text.muted" fontSize="13.5px">
        Unavailable
      </Text>
    );
  }

  if (evaluationStatus === 'in_progress') {
    return (
      <Flex align="center" gap={2}>
        <Spinner size="sm" />
        <Text color="compliance.text.muted" fontSize="13.5px">
          Evaluating…
        </Text>
      </Flex>
    );
  }

  if (issues.length === 0) {
    // Agent-facing surfaces show nothing rather than an empty shell. Without a
    // mismatch there is no finding to report, and "No evaluation data yet" only
    // ever raised questions for the reader (BLO-13465). The internal drawer
    // keeps the honest no-data state, where it is a debugging signal.
    if (
      !isExpanded ||
      (contextDocuments.length === 0 && checklistItemName == null)
    ) {
      return null;
    }

    // The one-line verdict ("No mismatches" / "No evaluation data yet") is the
    // card header's, not this body's: it stays readable while the card is
    // collapsed, which is how this state is usually seen. What follows is the
    // evidence behind that line.
    const isEvaluated = evaluationStatus === 'evaluated';

    return (
      <VStack align="stretch" spacing={2}>
        {checklistItemName != null ? (
          <Text
            color="compliance.text.primary"
            fontSize="14.5px"
            fontWeight="semibold"
            lineHeight={1.3}
            sx={PRETTY_WRAP}
          >
            {checklistItemName}
          </Text>
        ) : null}
        <ChecklistSatisfiedBy
          allowedDocumentCategories={requiredDocumentCategories}
          allowedForms={requiredForms.map((code) => ({ code, name: null }))}
        />
        {contextDocuments.length > 0 ? (
          <Box>
            <ChecklistBlockCaption
              count={
                isEvaluated
                  ? `${contextDocuments.length} attached · ${
                      contextDocuments.filter(
                        (document) =>
                          document.hasCurrentProblem ||
                          document.verdict ===
                            PlatformRequirementDocumentVerdict.MISMATCHED
                      ).length
                    } mismatch`
                  : String(contextDocuments.length)
              }
              label={isEvaluated ? 'Successful checks' : 'Classified documents'}
            />
            <VStack align="stretch" mt={1} spacing="2px">
              {contextDocuments.map((document) => {
                const isSelected =
                  document.id === selectedIngestionDocumentId ||
                  document.id === selectedPlatformDocumentId;

                return (
                  <AttachedDocumentRow
                    key={document.id}
                    contractDocuments={document.contractDocuments}
                    documentName={document.documentName}
                    evaluationStatus={evaluationStatus}
                    isProblemDocument={document.hasCurrentProblem}
                    isSelected={isSelected}
                    showIcon={!isEvaluated}
                    verdict={document.verdict}
                  />
                );
              })}
            </VStack>
          </Box>
        ) : null}
        {isEvaluated ? null : (
          <Text
            color="compliance.text.muted"
            fontSize="12.5px"
            lineHeight={1.4}
            sx={PRETTY_WRAP}
          >
            Requirement checks run on the next auto-review, or run one now.
          </Text>
        )}
      </VStack>
    );
  }

  const groups = groupChecklistIssues(issues);

  const renderGroup = (group: ChecklistGroup): ReactNode => {
    const state = deriveGroupState(group);

    return match(rowVariant)
      .with('compact', () => (
        <CompactChecklistRow
          group={group}
          showStatusChip={showStatusChip}
          state={state}
        />
      ))
      .with('suggestion', () => (
        <SuggestionChecklistRow
          actions={actions}
          group={group}
          showStatusChip={showStatusChip}
          state={state}
        />
      ))
      .with('expanded', () => (
        <ExpandedChecklistGroup
          actions={actions}
          evaluationStatus={evaluationStatus}
          group={group}
          selectedIngestionDocumentId={selectedIngestionDocumentId}
          selectedPlatformDocumentId={selectedPlatformDocumentId}
          showEvaluatedAt={showEvaluatedAt}
          showStatusChip={showStatusChip}
          state={state}
          onOpenDocument={onOpenDocument}
        />
      ))
      .exhaustive();
  };

  return (
    <VStack align="stretch" spacing={isExpanded ? '10px' : 1}>
      {groups.map((group) => (
        <Box key={group.renderGroupKey}>{renderGroup(group)}</Box>
      ))}
    </VStack>
  );
};
